import React, { useRef, useState, useEffect } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useFocusWithin } from "@react-aria/interactions";
import DropdownIcon from "@ecologi/assets/icons/SmallIcon/Dropdown.svg";
import { Typography } from "../Typography";
import { sharedMenuOptionStyles } from "./NavLink";
import { NavigationTag } from "../NavigationTag";
import { excludePropsFromForwarding } from "../utils";
import { NavigationTagType } from "./navigation";

const Button = styled.button`
  ${({ theme }) => sharedMenuOptionStyles({ isActive: false, theme })}

  &:hover {
    background: ${({ theme }) =>
      theme.colors.opacity(theme.colors.snowdrop, 0.03)};
  }
`;

const IconWrapper = styled.div`
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 0;
  top: 0;
  width: 50px;
`;

export const Icon = styled(DropdownIcon, excludePropsFromForwarding("isOpen"))<{
  isOpen: boolean;
}>`
  transition: transform 250ms ease;
  transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};

  path {
    stroke: ${({ theme }) => theme.colors.snowdrop};
  }
`;

export const Content = styled.div<{
  isOpen: boolean;
  scrollHeight: number;
}>`
  max-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) =>
    theme.colors.opacity(theme.colors.snowdrop, 0.05)};
  transition: max-height 250ms ease;

  ${({ isOpen, scrollHeight }) =>
    isOpen &&
    css`
      max-height: ${scrollHeight}px;
    `}
`;

interface NavDropdownProps {
  children: React.ReactNode;
  isOpen: boolean;
  label: string;
  tag?: NavigationTagType;
  onClick: () => void;
}

export function NavDropdown({
  children,
  isOpen,
  label,
  onClick,
  tag,
}: NavDropdownProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [focusWithin, setFocusWithin] = useState(false);

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  });

  useEffect(() => {
    if (contentRef && contentRef.current) {
      const { scrollHeight } = contentRef.current;
      setScrollHeight(scrollHeight);
    }
  }, [contentRef]);

  return (
    <>
      <Button onClick={onClick}>
        <Typography use="body1" as="p">
          {label}
        </Typography>
        {tag && (
          <NavigationTag themeColor={tag?.themeColor}>{tag.text}</NavigationTag>
        )}
        <IconWrapper>
          <Icon isOpen={isOpen} />
        </IconWrapper>
      </Button>
      <Content
        {...focusWithinProps}
        ref={contentRef}
        isOpen={focusWithin || isOpen}
        scrollHeight={scrollHeight}
      >
        {children}
      </Content>
    </>
  );
}
